import FormattedMessage from 'I18n/components/FormattedMessage';
import { BREEZE_TOOLTIP_DELAY } from 'chatspot-core/constants/Delays';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { Button } from 'foundations-components/transitional/components/Button';
import { ExpandableText } from 'foundations-components/transitional/components/ExpandableText';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { useMemo, useState } from 'react';
import { useIsUngated } from '../../hooks/useIsUngated';
import { gate } from 'hub-http/gates';
import CitationPill from './CitationPill';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CitationBadge = ({
  text
}) => {
  return /*#__PURE__*/_jsx(Badge, {
    use: "koala",
    children: /*#__PURE__*/_jsx(Paragraph, {
      margin: "0",
      textTransform: "none",
      children: text
    })
  });
};
const CitationCardHeader = ({
  icon,
  title,
  locationText,
  sourceName,
  textDecoration
}) => /*#__PURE__*/_jsx(Flex, {
  flexDirection: "column",
  gap: "100",
  paddingBlock: "400",
  paddingInline: "500",
  backgroundColor: "fill-surface-recessed",
  flexGrow: 1,
  borderRadius: "300",
  children: /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "row",
    gap: "200",
    alignItems: "center",
    children: [icon, /*#__PURE__*/_jsxs(Flex, {
      gap: "50",
      flexDirection: "column",
      justifyContent: "center",
      children: [title && /*#__PURE__*/_jsx(Text, {
        typography: "heading-50",
        textDecoration: textDecoration !== null && textDecoration !== void 0 ? textDecoration : 'none',
        textAlign: "left",
        children: /*#__PURE__*/_jsx(TruncateString, {
          children: title
        })
      }), (sourceName || locationText) && (locationText ? /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "row",
        gap: "200",
        alignItems: "center",
        children: [/*#__PURE__*/_jsx(Text, {
          typography: "detail-100",
          textAlign: "left",
          children: sourceName || /*#__PURE__*/_jsx(FormattedMessage, {
            message: "aiComponentsUiLibrary.components.citation.source"
          })
        }), /*#__PURE__*/_jsx(CitationPill, {
          label: locationText,
          variant: "outlined"
        })]
      }) : /*#__PURE__*/_jsx(Text, {
        typography: "detail-100",
        textAlign: "left",
        children: sourceName || /*#__PURE__*/_jsx(FormattedMessage, {
          message: "aiComponentsUiLibrary.components.citation.source"
        })
      }))]
    })]
  })
});
const CitationCard = ({
  // Header props
  title,
  locationText,
  sourceName,
  icon,
  // Badge props
  badges = [],
  // Content props
  summary,
  // Feedback props
  feedbackComponent,
  // Interaction props
  onClick,
  // Layout props
  maxBlockSize
}) => {
  const isClickable = !!onClick;
  const [isHovered, setIsHovered] = useState(false);
  const isUngatedForLocationWithinSource = useIsUngated(gate('KnowledgeBox:LocationWithinSource'));
  const handleMouseEnter = () => {
    setIsHovered(true);
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
  };
  const normalizedBadges = useMemo(() => {
    return badges.map(badge => {
      if (typeof badge === 'string') {
        return {
          text: badge
        };
      }
      return badge;
    });
  }, [badges]);
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    gap: "400",
    paddingBlockEnd: "400",
    maxBlockSize: maxBlockSize,
    overflowY: "auto",
    paddingBlockStart: "200",
    paddingInline: "200",
    children: [isClickable ? /*#__PURE__*/_jsx(Button, {
      use: "unstyled",
      onClick: onClick,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      block: true,
      width: "100%",
      children: /*#__PURE__*/_jsx(CitationCardHeader, {
        icon: icon,
        title: title,
        locationText: isUngatedForLocationWithinSource && locationText ? locationText : undefined,
        sourceName: sourceName,
        textDecoration: isHovered ? 'underline' : 'none'
      })
    }) : /*#__PURE__*/_jsx(CitationCardHeader, {
      icon: icon,
      title: title,
      locationText: isUngatedForLocationWithinSource && locationText ? locationText : undefined,
      sourceName: sourceName
    }), normalizedBadges.length > 0 && /*#__PURE__*/_jsx(View, {
      paddingInline: "400",
      children: /*#__PURE__*/_jsx(Flex, {
        flexDirection: "row",
        gap: "200",
        alignItems: "center",
        flexWrap: "wrap",
        children: normalizedBadges.map((badge, index) => {
          return badge.tooltip ? /*#__PURE__*/_jsx(Tooltip, {
            delay: BREEZE_TOOLTIP_DELAY,
            title: badge.tooltip,
            children: /*#__PURE__*/_jsx(CitationBadge, {
              text: badge.text
            }, index)
          }, index) : /*#__PURE__*/_jsx(CitationBadge, {
            text: badge.text
          }, index);
        })
      })
    }), summary && /*#__PURE__*/_jsx(View, {
      paddingInline: "400",
      children: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        gap: "200",
        children: [/*#__PURE__*/_jsx(Text, {
          as: "h3",
          typography: "heading-50",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "aiComponentsUiLibrary.components.citation.summary"
          })
        }), /*#__PURE__*/_jsx(ExpandableText, {
          hideButtonCaret: true,
          buttonAlign: "center",
          use: "default",
          children: summary
        })]
      })
    }), feedbackComponent && /*#__PURE__*/_jsx(View, {
      paddingInline: "400",
      children: feedbackComponent
    })]
  });
};
CitationCard.displayName = 'CitationCard';
export default CitationCard;